import { Mapper } from '@intelliarts/ia-react-template';

import {
  assertTurnoverApiValid,
  turnoverDashboardApiBodySchema,
  turnoverYearlyTrendEnvelopeSchema,
} from './turnoverSchemas';
import type {
  TurnoverDashboardApiBody,
  TurnoverYearSnapshot,
  TurnoverYearlyDatum,
  TurnoverYearlyTrendApiResponse,
} from './turnoverTypes';

function withProjectDefaults<T extends {
  voluntaryLeft?: number;
  involuntaryLeft?: number;
  headcountAtStart?: number;
  headcountAtEnd?: number;
}>(row: T) {
  return {
    ...row,
    voluntaryLeft: row.voluntaryLeft ?? 0,
    involuntaryLeft: row.involuntaryLeft ?? 0,
    headcountAtStart: row.headcountAtStart ?? 0,
    headcountAtEnd: row.headcountAtEnd ?? 0,
  };
}

function parseTurnoverDashboardBody(body: TurnoverDashboardApiBody): TurnoverYearSnapshot {
  const parsed = assertTurnoverApiValid(turnoverDashboardApiBodySchema, body);
  const trend = parsed.yearlyTrend;
  return {
    year: parsed.year,
    employeesAtStart: parsed.employeesAtStart,
    employeesAtEnd: parsed.employeesAtEnd,
    employeesWhoLeft: parsed.employeesWhoLeft,
    voluntaryLeft: parsed.voluntaryLeft,
    involuntaryLeft: parsed.involuntaryLeft,
    probationFailures: parsed.probationFailures,
    newHires: parsed.newHires,
    newHiresLeftWithinFirstYear: parsed.newHiresLeftWithinFirstYear,
    reasons: {
      voluntary: parsed.reasons.voluntary,
      involuntary: parsed.reasons.involuntary,
      notSpecified: parsed.reasons.notSpecified ?? 0,
      voluntaryReasons: parsed.reasons.voluntaryReasons ?? [],
      involuntaryReasons: parsed.reasons.involuntaryReasons ?? [],
    },
    mom: parsed.mom,
    projects: parsed.projects.map(withProjectDefaults),
    projectGroups: (parsed.projectGroups ?? []).map(withProjectDefaults),
    yearlyTrend: trend != null && trend.length > 0 ? trend : [],
    // Both stay undefined against an older backend; the consumers degrade rather than substitute a zero.
    priorYearToDate: parsed.priorYearToDate ?? undefined,
    reasonVocabularySize: parsed.meta?.reasonVocabularySize ?? undefined,
  };
}

export const turnoverDashboardMapper: Mapper<TurnoverYearSnapshot, TurnoverDashboardApiBody> = {
  toModel: entity => {
    if (entity == null || typeof entity !== 'object') {
      return undefined as unknown as TurnoverYearSnapshot;
    }
    return parseTurnoverDashboardBody(entity);
  },
  toEntity: model => ({
    ...model,
    ...(model.yearlyTrend?.length ? { yearlyTrend: model.yearlyTrend } : {}),
  }),
};

export const turnoverYearlyTrendEnvelopeMapper: Mapper<
  TurnoverYearlyDatum[],
  TurnoverYearlyTrendApiResponse
> = {
  toModel: entity => assertTurnoverApiValid(turnoverYearlyTrendEnvelopeSchema, entity).data,
  toEntity: points => ({ data: points }),
};
